import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { translate } from 'helpers/translateHelper';

import { gatewayMenuItems, gatewayType, INSTALLATION_REPORT_NETA } from './constants';
import { find } from 'lodash';
import { activateDeactivateGateway, downloadInstallationReport, downloadDiagnosticReport } from './helpers';
import { RefreshGateway } from './RefreshGateway';
import { ActivateDeactivateGateway } from './ActivateDeactivateGateway';
import { DeleteGatewayComponent } from './DeleteGateway';
import { UpdateFirmware } from './UpdateFirmware';
import { RebootGateway } from './RebootGateway';
import useGatewayUserCapability from 'sharedHooks/useGatewayUserCapability';
import { requestType } from './ActivateDeactivateGateway/constants';
import { ExportMessageArchive } from './ExportMessageArchive';


const useHeaderMenus = (gatewayID, gatewayInformation, remoteConnectionRequestType) => {
    const userCapability = useGatewayUserCapability();

    const isCapabilitiesLoaded = userCapability && !userCapability.isLoading?.() && userCapability.isSet?.();

    const headerMenus = useMemo(() => {
        if (!isCapabilitiesLoaded) {
            return [];
        }
        const allHeaderMenus = [
            {
                id: gatewayMenuItems.REBOOT_GATEWAY,
                capabilityCheck: () => userCapability.hasRebootGateway(),
                title: translate('ABB.Powertrain.Frontend.gatewayRebootTitle'),
                modalContentComponent: RebootGateway,
                hidden: !userCapability.hasRebootGateway()
            },
            {
                id: gatewayMenuItems.UPDATE_FIRMWARE,
                capabilityCheck: () => userCapability.hasGatewayFirmwareUpdateTrigger(),
                title: translate('ABB.Powertrain.Frontend.gatewayUpdateFirmwareTitle'),
                modalContentComponent: UpdateFirmware
            },
            {
                id: gatewayMenuItems.REFRESH_NETA,
                capabilityCheck: () => userCapability.hasResetGateway(),
                title: translate('ABB.Powertrain.Frontend.gatewayRefreshNetaTitle'),
                modalContentComponent: RefreshGateway
            },
            {
                id: gatewayMenuItems.DOWNLOAD_INSTALLATION_REPORT,
                capabilityCheck: () => userCapability.hasGatewayDiagnosticTrigger(),
                title: translate('ABB.Powertrain.Frontend.gatewayDownloadInstallationReportTitle'),
                menuAction: (dispatch, gatewayID) => downloadInstallationReport(dispatch, gatewayID, INSTALLATION_REPORT_NETA)
            },
            {
                id: gatewayMenuItems.DOWNLOAD_DIAGNOSTIC_REPORT,
                capabilityCheck: () => userCapability.hasIotDiagnosticReport(),
                title: translate('ABB.Powertrain.Frontend.gatewayDownloadDiagnosticReportTitle'),
                menuAction: (dispatch, gatewayID) => downloadDiagnosticReport(dispatch, gatewayID)
            },
            {
                id: gatewayMenuItems.ACTIVATE_REMOTE_CONNECTION,
                capabilityCheck: () => userCapability.hasActivateRemoteConnection(),
                title: translate('ABB.Powertrain.Frontend.gatewayRemoteConnectionActivateTitle'),
                menuAction: (dispatch, gatewayInformation) => {
                    activateDeactivateGateway(dispatch, gatewayInformation, true);
                },
                hidden: remoteConnectionRequestType === requestType.ACTIVATED_REMOTE_CONNECTION || remoteConnectionRequestType === null
            },
            {
                id: gatewayMenuItems.DEACTIVATE_REMOTE_CONNECTION,
                capabilityCheck: () => userCapability.hasActivateRemoteConnection(),
                title: translate('ABB.Powertrain.Frontend.gatewayRemoteConnectionDeactivateTitle'),
                modalContentComponent: ActivateDeactivateGateway,
                hidden: remoteConnectionRequestType === requestType.DEACTIVATED_REMOTE_CONNECTION
            },
            {
                id: gatewayMenuItems.DELETE_GATEWAY,
                capabilityCheck: () => userCapability.hasAssetAdminCapability(),
                title: translate('ABB.Powertrain.Frontend.gatewayDeleteTitle'),
                modalContentComponent: DeleteGatewayComponent
            },
            {
                id: gatewayMenuItems.EXPORT_MESSAGE_ARCHIVE,
                capabilityCheck: () => userCapability.hasExtractMessageArchive(),
                title: translate('ABB.Powertrain.Frontend.gatewayExportMessageArchiveTitle'),
                modalContentComponent: ExportMessageArchive,
                hidden: gatewayInformation.gatewayInfo?.gatewayType === gatewayType.UNKNOWN || gatewayInformation.gatewayInfo?.gatewayType === gatewayType.CASSIA || gatewayInformation.gatewayInfo?.gatewayType === gatewayType.ARUBA || gatewayInformation.gatewayInfo?.gatewayType === gatewayType.MACHSENSER
            }
        ];

        // Filter menus based on capabilities
        return allHeaderMenus
            .filter(menu => menu.capabilityCheck() && menu.hidden !== true)
            .map(menu => ({
                id: menu.id,
                availableCapabilityFn: menu.capabilityCheck,
                title: menu.title,
                modalContentComponent: menu.modalContentComponent,
                menuAction: menu.menuAction,
                hidden: menu.hidden,
                disabled: !gatewayID
            }));
    }, [userCapability, gatewayID, isCapabilitiesLoaded, remoteConnectionRequestType]);

    const [selectedHeaderMenu, setSelectedHeaderMenu] = useState(null);
    const selectedMenuItemId = useSelector(({ powertrainTools }) => powertrainTools?.gateway?.selectedMenuItem);

    useEffect(() => {
        setSelectedHeaderMenu(find(headerMenus, { id: selectedMenuItemId }));
    }, [selectedMenuItemId]);

    return [headerMenus, selectedHeaderMenu];
};

export default useHeaderMenus;
